import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { rootRoute } from './routes/root'
import { indexRoute } from './routes/index'
import { authRoute } from './routes/auth'
import { trainerRoute } from './routes/trainer'
import { dailyCardRoute } from './routes/daily-card'
import { dailyPicksRoute } from './routes/daily-picks'
import { deckNewRoute, deckRoute, decksRoute } from './routes/decks'
import { usersRoute } from './routes/users'
import { auditLogRoute } from './routes/audit-log'
import { userCardRoute } from './routes/user-card'
import { overviewRoute } from './routes/overview'
import { lunarsRoute } from './routes/lunars'
import { cardRatingsRoute } from './routes/card-ratings'
import { arcanaReadingsRoute } from './routes/arcana-readings'
import { readingTopicsRoute } from './routes/reading-topics'
import { notificationsRoute } from './routes/notifications'
import { initAppUpdate, navigateIfOutdated } from '@lunary/ui-web/app-update'
import { initDeck } from '@lunary/ui-web/deck'
import { setBrandStage } from '@lunary/ui-web/brand-stage'
import { getAccessToken } from './shared/api/client'
import { initTheme } from './shared/theme/theme'
import '@lunary/ui-web/fonts.css'
import '@lunary/ui-web/scroll.css'
import '@lunary/ui-web/sortable.css'
import '@lunary/ui-web/touch-inputs.css'
import './styles/globals.css'

setBrandStage(import.meta.env.VITE_APP_STAGE)
initTheme()
initAppUpdate({ buildId: __BUILD_ID__ })
initDeck('/api/v1', getAccessToken)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
    },
  },
})

const routeTree = rootRoute.addChildren([indexRoute, authRoute, trainerRoute, dailyCardRoute, dailyPicksRoute, decksRoute, deckNewRoute, deckRoute, arcanaReadingsRoute, readingTopicsRoute, notificationsRoute, usersRoute, userCardRoute, overviewRoute, lunarsRoute, cardRatingsRoute, auditLogRoute])

const router = createRouter({ routeTree, defaultPreload: 'intent' })

router.subscribe('onBeforeNavigate', ({ toLocation, pathChanged }) => {
  if (pathChanged) navigateIfOutdated(toLocation.href)
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  )
}

const root = document.getElementById('root')
if (root) {
  createRoot(root).render(
    <StrictMode>
      <App />
    </StrictMode>,
  )
}
